/* eslint-disable @typescript-eslint/no-explicit-any */
import { useRef, useState } from 'react';
import styled from 'styled-components';
import ChartLabel from 'components/Label/ChartLabel';
import PersonnelModalLayout from 'components/Modal/PersonnelModal/PersonnelModalLayout';
import useLineForDoughnut from 'hooks/useLineForDoughnut';
import { chartColor } from 'asserts/styles/colors';
import { color } from 'asserts/styles/colors';
import { commonTooltipOptions } from 'consts/chartOptions';
import { useAtom } from 'jotai';
import productAtom from 'atom/productAtom';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import ModalPortal from 'components/Modal/ModalPortal';
import { CSSTransition } from 'react-transition-group';

ChartJS.register(ArcElement, Tooltip, Legend);

interface ChartDataProps {
  extractedData: any[][];
  extractedLabels: string[][];
  statsDifferentProductNumber: any[];
}

const {
  graphGreen,
  graphOrange,
  graphYellow,
  graphRed,
  graphViolet,
  graphGrey
} = chartColor;

const colors = [
  graphGreen,
  graphYellow,
  graphOrange,
  graphRed,
  graphViolet,
  graphGrey
];

const PersonnelStatisticsCharts = ({
  extractedData,
  extractedLabels,
  statsDifferentProductNumber
}: ChartDataProps) => {
  const nodeRef = useRef<HTMLDivElement>(null);
  const [productNumber] = useAtom(productAtom);

  const defaultTypes = [
    {
      title: statsDifferentProductNumber[0].value,
      data: extractedData[0],
      label: extractedLabels[0],
      backgroundColor: colors,
      borderColor: colors
    },
    {
      title: statsDifferentProductNumber[1].value,
      data: extractedData[1],
      label: extractedLabels[1],
      backgroundColor: colors,
      borderColor: colors
    }
  ];

  if (productNumber === 4 || productNumber === 5) {
    const ThridTypes = [
      {
        title: statsDifferentProductNumber[2].value,
        data: extractedData[2],
        label: extractedLabels[2],
        backgroundColor: colors,
        borderColor: colors
      }
    ];

    defaultTypes.push(...ThridTypes);
  }

  const [selectedChartType, setSelectedChartType] = useState<string | null>(
    null
  );
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);
  const [selectedData, setSelectedData] = useState<string | null>(null);
  const [isOPenModal, setIsOpenModal] = useState<boolean>(false);

  const handleClickOpenModal = () => {
    setIsOpenModal(true);
  };

  const handleClickCloseModal = () => {
    setIsOpenModal(false);
  };

  const handleClickChart = (label: string, value: number, index: number) => {
    const selectedPersonnelType = statsDifferentProductNumber[index].key;

    setSelectedChartType(selectedPersonnelType);
    setSelectedLabel(label);

    statsDifferentProductNumber[index].key;

    setSelectedData(statsDifferentProductNumber[index].innerKey[value].key);

    handleClickOpenModal();
  };

  const options = {
    radius: '50%',
    layout: {
      padding: 20
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: commonTooltipOptions
    }
  };

  return (
    <>
      <Wrapper>
        {defaultTypes.map((personnel, index) => (
          <PersonnelChart
            data={personnel.data}
            key={index}
            title={personnel.title}
            labels={personnel.label}
            borderColor={personnel.borderColor}
            backgroundColor={personnel.backgroundColor}
            options={options}
            productNumber={productNumber}
            onClickChart={(label: string, value: number) =>
              handleClickChart(label, value, index)
            }
          />
        ))}
      </Wrapper>
      <CSSTransition
        in={isOPenModal}
        classNames="modal"
        nodeRef={nodeRef}
        timeout={300}
        unmountOnExit
      >
        <ModalPortal>
          <PersonnelModalLayout
            selectedLabel={selectedLabel}
            selectedData={selectedData}
            selectedChartType={selectedChartType}
            onClickModal={handleClickCloseModal}
          />
        </ModalPortal>
      </CSSTransition>
    </>
  );
};

const PersonnelChart = ({
  title,
  options,
  labels,
  data: chartData,
  borderColor,
  backgroundColor,
  onClickChart,
  productNumber
}: any) => {
  const data = {
    labels,
    datasets: [
      {
        label: `${title}`,
        data: chartData,
        backgroundColor: backgroundColor,
        borderColor: borderColor,
        cutout: '65%'
      }
    ]
  };

  // todo: 함수명 변경
  const handleClickChart = (_: any, elements: any) => {
    if (elements.length > 0) {
      const clickedIndex = elements[0].index;
      const clickedLabel = labels[clickedIndex];

      onClickChart(clickedLabel, clickedIndex);
    }
  };

  const handleHoverChart = (event: any, elements: any) => {
    event.native.target.style.cursor = elements[0] ? 'pointer' : 'default';
  };

  const labelsPlugin = {
    id: 'labelsPlugin',
    afterDatasetsDraw: useLineForDoughnut()
  };

  const uniqueLabels: string[] = Array.from(new Set(labels));
  const uniqueColos: string[] = Array.from(new Set(colors));

  return (
    <ChartBox $productNumber={productNumber}>
      <Title>{title}</Title>
      <Doughnut
        data={data}
        options={{
          ...options,
          onClick: handleClickChart,
          onHover: handleHoverChart
        }}
        plugins={[labelsPlugin]}
      />
      <ChartLabelWrapper $title={title} $productNumber={productNumber}>
        {uniqueLabels?.map((label: string, index: number) => (
          <ChartLabel
            key={`${title}${label}`}
            pointColor={uniqueColos[index]}
            text={label}
          />
        ))}
      </ChartLabelWrapper>
    </ChartBox>
  );
};

export default PersonnelStatisticsCharts;

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  position: relative;
  gap: 16px;
`;

const Title = styled.div`
  position: absolute;
  top: 32px;
  left: 50%;
  transform: translateX(-50%);
  font-weight: 500;
`;

const ChartBox = styled.div<{ $productNumber: number }>`
  display: flex;
  justify-content: center;
  position: relative;
  width: ${(props) =>
    props.$productNumber === 4 || props.$productNumber === 5 ? '395px' : '50%'};
  height: 460px;
  background: ${color.white005};
  border-radius: 16px;

  transition: background-color 0.25s ease-in-out;

  &:hover {
    background-color: ${color.white01};
  }
`;

const ChartLabelWrapper = styled.div<{
  $title: string;
  $productNumber: number;
}>`
  display: flex;
  align-items: center;
  justify-content: ${(props) =>
    props.title === '근속연수' ? 'initial' : 'center'};
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px 24px;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 0;
  width: ${(props) =>
    props.$productNumber === 4 || props.$productNumber === 5
      ? '400px'
      : '460px'};
  padding: 0 0 30px 24px;

  & div {
    width: 96px;
  }
`;
